import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Progress } from '@/shared/components/ui/progress';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { useDeleteProduct } from '@/shared/hooks/api/useWorkspace';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useQuery } from '@tanstack/react-query';
import { WorkspaceService } from '@/features/Workspaces/services/workspaceService';

interface ProductDangerZoneProps {
  workspaceId: string;
}

export function WorkspaceDangerZone({ workspaceId }: ProductDangerZoneProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { reloadUserProducts } = useWorkspace();
  const { mutateAsync: deleteProductAsync } = useDeleteProduct();

  const [confirmName, setConfirmName] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout>();

  const { data: workspace } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => WorkspaceService.getById(workspaceId),
    enabled: !!workspaceId,
  });

  const workspaceName = workspace?.name || '';

  // Animate progress bar
  useEffect(() => {
    if (isDeleting) {
      setDeleteProgress(0);
      let current = 0;
      progressIntervalRef.current = setInterval(() => {
        current += Math.random() * 15;
        if (current > 90) current = 90;
        setDeleteProgress(current);
      }, 300);
    } else {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setDeleteProgress(0);
    }
    return () => { if (progressIntervalRef.current) clearInterval(progressIntervalRef.current); };
  }, [isDeleting]);

  const handleDelete = async () => {
    if (confirmName !== workspaceName) return;

    // Backend cascateia agentes/KBs/embeddings ao excluir o produto (paridade
    // legacy). Sem pre-check de bloqueio — a confirmação por nome já protege.
    setIsConfirmOpen(false);
    setIsDeleting(true);

    try {
      await deleteProductAsync(workspaceId);

      setDeleteProgress(100);
      await new Promise(resolve => setTimeout(resolve, 400));

      await queryClient.invalidateQueries({ queryKey: ['current-organization-products'] });
      await reloadUserProducts();

      toast({
        title: t('settings.workspaces.deleteSuccess'),
        description: t('settings.workspaces.deleteSuccessDescription'),
      });

      navigate('/settings?tab=products');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('settings.workspaces.deleteError'),
        description: resolveApiErrorMessage(error, t),
      });
    } finally {
      setIsDeleting(false);
      setConfirmName('');
    }
  };

  return (
    <>
      <Card className="border-destructive/20">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-destructive">{t('workspaces.configPage.dangerZone.title', 'Zona de Perigo')}</CardTitle>
              <CardDescription className="mt-1">
                {t('workspaces.configPage.dangerZone.description', 'Ações irreversíveis para este produto.')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border border-destructive/20 p-4">
            <div>
              <p className="font-medium">{t('workspaces.configPage.dangerZone.deleteTitle', 'Excluir produto')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('workspaces.configPage.dangerZone.deleteDescription', 'Esta ação é permanente e não pode ser desfeita. Todos os dados do produto serão removidos.')}
              </p>
            </div>
            <Button variant="destructive" onClick={() => setIsConfirmOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              {t('workspaces.configPage.dangerZone.deleteButton', 'Excluir')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={(open) => { setIsConfirmOpen(open); if (!open) setConfirmName(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspaces.configPage.dangerZone.confirmTitle', 'Confirmar exclusão do produto')}</DialogTitle>
            <DialogDescription>
              {t('workspaces.configPage.dangerZone.confirmDescription', 'Esta ação é irreversível. Digite o nome do produto para confirmar.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{t('workspaces.configPage.dangerZone.confirmLabel', 'Digite o nome do produto para confirmar:')}</Label>
              <Input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={workspaceName}
              />
              <p className="text-xs text-muted-foreground">
                {t('workspaces.configPage.dangerZone.confirmHint', 'Digite "{{name}}" para confirmar', { name: workspaceName })}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsConfirmOpen(false); setConfirmName(''); }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={confirmName !== workspaceName}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('workspaces.configPage.dangerZone.deleteButton', 'Excluir')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress dialog */}
      <Dialog open={isDeleting}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader className="text-center">
            <DialogTitle className="text-center">
              {t('settings.workspaces.deletingWorkspace')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('settings.workspaces.deletingWorkspaceMessage', { workspaceName })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{deleteProgress < 100 ? t('common.processing', 'Processando...') : t('common.done', 'Concluído')}</span>
                <span className="font-medium">{Math.round(deleteProgress)}%</span>
              </div>
              <Progress value={deleteProgress} className="h-2" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
